import React from "react";
import {
    Box, Button, Card, CardContent, CircularProgress, Paper, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import type { Transaction } from "../../../../hooks/Clients/dashboard";
import { TabPanel, TooltipCell, TooltipHeaderCell, dataCellSx, formatAmount, formatDateWithHours, getAccCumul, getAccValues, pickRelevantAccounts } from "./ui";

const DateTimeCell = ({ value }: { value: string | null | undefined }) => {
    const formatted = formatDateWithHours(value);
    const [datePart, timePart] = formatted.split(" ");
    return (
        <TableCell sx={{ ...dataCellSx, lineHeight: 1.2 }}>
            <Typography variant="body2" noWrap sx={{ fontSize: "inherit" }}>{datePart}</Typography>
            {timePart && (
                <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: "0.68rem" }}>
                    {timePart}
                </Typography>
            )}
        </TableCell>
    );
};

interface TransactionsTabProps {
    activeTab: number;
    transactions: Transaction[];
    loadingTransac: boolean;
    transacPage: number;
    setTransacPage: (page: number) => void;
    transacRowsPerPage: number;
    setTransacRowsPerPage: (rows: number) => void;
    setSelectedTx: (tx: Transaction | null) => void;
    // Absent si l'utilisateur n'a pas le droit : le bouton est masqué
    handleOpenManualTransac?: () => void;
    // Absent si l'utilisateur n'a pas le droit : le bouton est masqué
    handleOpenMvt?: () => void;
    t: (key: string) => string;
}

const TransactionsTab = ({
    activeTab, transactions, loadingTransac,
    transacPage, setTransacPage, transacRowsPerPage, setTransacRowsPerPage,
    setSelectedTx, handleOpenManualTransac, handleOpenMvt, t
}: TransactionsTabProps) => (
    <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1.5 }}>
            {handleOpenMvt && (
                <Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenMvt}>
                    Mouvement de points
                </Button>
            )}
            {handleOpenManualTransac && (
                <Button variant="contained" size="small" startIcon={<PointOfSaleIcon />} onClick={handleOpenManualTransac}>
                    Ajouter une transaction
                </Button>
            )}
        </Box>
        {loadingTransac ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        ) : transactions.length === 0 ? (
            <Card variant="outlined">
                <CardContent sx={{ textAlign: "center", py: 6 }}>
                    <ReceiptLongIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">Aucune transaction trouvée</Typography>
                </CardContent>
            </Card>
        ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
                <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
                    <colgroup>
                        <col style={{ width: "110px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "90px" }} />
                        <col style={{ width: "120px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "120px" }} />
                        <col style={{ width: "80px" }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TooltipHeaderCell>Num identifiant</TooltipHeaderCell>
                            <TooltipHeaderCell>Date de réception</TooltipHeaderCell>
                            <TooltipHeaderCell>Date Achat</TooltipHeaderCell>
                            <TooltipHeaderCell>Magasin</TooltipHeaderCell>
                            <TooltipHeaderCell>Montant payé</TooltipHeaderCell>
                            <TooltipHeaderCell>Ticket</TooltipHeaderCell>
                            <TooltipHeaderCell>Pts Acquis</TooltipHeaderCell>
                            <TooltipHeaderCell>Cumul Pts</TooltipHeaderCell>
                            <TooltipHeaderCell>Évènement</TooltipHeaderCell>
                            <TooltipHeaderCell sx={{ textAlign: "center" }}>Type</TooltipHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions
                            .slice(transacPage * transacRowsPerPage, (transacPage + 1) * transacRowsPerPage)
                            .map((tx, idx) => {
                                const hasOffer = Number(tx.COUNTLINKOFFER ?? 0) > 0;
                                const accValues = pickRelevantAccounts(getAccValues(tx));
                                const accCumul = pickRelevantAccounts(getAccCumul(tx));
                                return (
                                    <TableRow
                                        key={tx.RECEIPTNUMBER || idx}
                                        hover
                                        onClick={() => setSelectedTx(tx)}
                                        sx={{
                                            cursor: "pointer",
                                            bgcolor: hasOffer ? "rgba(237, 108, 2, 0.35)" : undefined,
                                            "&:hover": { bgcolor: hasOffer ? "rgba(237, 108, 2, 0.5) !important" : undefined },
                                        }}
                                    >
                                        <TooltipCell label={String(tx.IDENTIFIER_PERSON || "")}>{tx.IDENTIFIER_PERSON || "—"}</TooltipCell>
                                        <DateTimeCell value={tx.VALIDATIONDATE} />
                                        <DateTimeCell value={tx.TRANSACTIONDATE} />
                                        <TooltipCell label={String(tx.STORE_NAME || tx.STORE || "")}>{tx.STORE_NAME || tx.STORE || "—"}</TooltipCell>
                                        <TableCell sx={dataCellSx}>
                                            <Typography variant="body2" fontWeight={600} color="primary.main" noWrap sx={{ fontSize: "inherit" }}>
                                                {formatAmount(tx.AMOUNT as number | string | null)}
                                            </Typography>
                                        </TableCell>
                                        <TooltipCell label={String(tx.RECEIPTNUMBER || "")}>{tx.RECEIPTNUMBER || "—"}</TooltipCell>
                                        <TableCell sx={dataCellSx}>
                                            {accValues.length === 0 ? "—"
                                            : accValues.length === 1 ? (
                                                <Typography variant="body2" fontWeight={600} color="primary.main" noWrap sx={{ fontSize: "inherit" }}>
                                                    {accValues[0].value}
                                                </Typography>
                                            ) : (
                                                <Tooltip arrow placement="top" title={
                                                    <Box sx={{ p: 0.5 }}>
                                                        {accValues.map((acc, i) => (
                                                            <Typography key={i} variant="caption" display="block" fontWeight={600}>
                                                                {acc.value} <Box component="span" sx={{ fontWeight: 400, opacity: 0.8 }}>({acc.head})</Box>
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                }>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "default" }}>
                                                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "primary.main" }} />
                                                        <Typography variant="caption" color="primary.main" fontWeight={600}>{accValues.length}</Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell sx={dataCellSx}>
                                            {accCumul.length === 0 ? "—"
                                            : accCumul.length === 1 ? (
                                                <Typography variant="body2" noWrap sx={{ fontSize: "inherit" }}>
                                                    {accCumul[0].value}
                                                </Typography>
                                            ) : (
                                                <Tooltip arrow placement="top" title={
                                                    <Box sx={{ p: 0.5 }}>
                                                        {accCumul.map((acc, i) => (
                                                            <Typography key={i} variant="caption" display="block" fontWeight={600}>
                                                                {acc.value} <Box component="span" sx={{ fontWeight: 400, opacity: 0.8 }}>({acc.head})</Box>
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                }>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "default" }}>
                                                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{accCumul.length}</Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TooltipCell label={String(tx.EVENEMENT || "")}>{tx.EVENEMENT || "—"}</TooltipCell>
                                        <TableCell sx={{ ...dataCellSx, overflow: "visible", textAlign: "center" }}>
                                            {tx.TRANSACTIONSOURCE ? (
                                                <Tooltip arrow title={String(tx.TRANSACTIONSOURCE)}>
                                                    <Box component="span" sx={{
                                                        display: "inline-block",
                                                        px: 0.75, py: 0.1,
                                                        borderRadius: 1,
                                                        bgcolor: "grey.100",
                                                        fontSize: "0.72rem",
                                                        fontWeight: 600,
                                                        color: "text.secondary",
                                                        letterSpacing: "0.02em",
                                                    }}>
                                                        {String(tx.TRANSACTIONSOURCE)}
                                                    </Box>
                                                </Tooltip>
                                            ) : "—"}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={transactions.length}
                    page={transacPage}
                    onPageChange={(_, p) => setTransacPage(p)}
                    rowsPerPage={transacRowsPerPage}
                    onRowsPerPageChange={(e) => { setTransacRowsPerPage(parseInt(e.target.value, 10)); setTransacPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Lignes par page :"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
                />
            </TableContainer>
        )}
    </TabPanel>
);

export default TransactionsTab;
